// Montagem da cartela do vendedor (arte + QR + nome) toda no navegador.
// Compartilhada entre o talão do vendedor (/meu-talao) e o painel do evento.

export interface CartelaCaixa {
  x: number;
  y: number;
  largura: number;
  altura: number;
}

export interface CartelaDados {
  vendedor_id: number;
  nome: string;
  token: string | null;
  faixa: string | null;
  link: string | null;
  arte_url: string | null;
  caixa_qr: CartelaCaixa;
  caixa_nome: CartelaCaixa;
  evento: string | null;
  valor_cota_reais: number | null;
  arquivo: string;
  mensagem: string;
}

// Carrega a arte com CORS habilitado (necessário para o `toBlob` não falhar).
export function carregarImagem(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Não consegui carregar a arte.'));
    img.src = url;
  });
}

// Desenha o nome do vendedor, em maiúsculas, centralizado na caixa indicada.
function desenharNome(
  ctx: CanvasRenderingContext2D,
  nome: string,
  x: number,
  y: number,
  w: number,
  h: number,
) {
  const maxLargura = w * 0.92;
  const minimo = 12;
  const fonteBase = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

  let texto = (nome || '').trim().toUpperCase();
  if (!texto) texto = 'VENDEDOR';

  const cabe = (tamanho: number, t: string) => {
    ctx.font = `bold ${tamanho}px ${fonteBase}`;
    return ctx.measureText(t).width <= maxLargura;
  };

  let tamanho = h * 0.62;
  while (tamanho > minimo && !cabe(tamanho, texto)) tamanho -= 1;

  if (!cabe(tamanho, texto)) {
    const partes = texto.split(/\s+/).filter(Boolean);
    if (partes.length > 2) texto = `${partes[0]} ${partes[partes.length - 1]}`;
    tamanho = h * 0.62;
    while (tamanho > minimo && !cabe(tamanho, texto)) tamanho -= 1;
    if (!cabe(tamanho, texto)) tamanho = minimo;
  }

  ctx.save();
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = '#1a3c96';
  ctx.font = `bold ${tamanho}px ${fonteBase}`;
  ctx.fillText(texto, x + w / 2, y + h / 2);
  ctx.restore();
}

// Monta a cartela: arte + QR (dentro da moldura) + nome (no campo branco).
export async function montarCartela(
  img: HTMLImageElement,
  qr: HTMLCanvasElement,
  c: CartelaDados,
): Promise<Blob> {
  const W = img.naturalWidth;
  const H = img.naturalHeight;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Não consegui montar a cartela.');

  ctx.drawImage(img, 0, 0, W, H);

  // QR, quadrado, centralizado na caixa indicada.
  const q = c.caixa_qr;
  const bx = (q.x / 100) * W;
  const by = (q.y / 100) * H;
  const bw = (q.largura / 100) * W;
  const bh = (q.altura / 100) * H;
  const lado = Math.min(bw, bh) * 0.94;
  ctx.drawImage(qr, bx + (bw - lado) / 2, by + (bh - lado) / 2, lado, lado);

  // Nome.
  const n = c.caixa_nome;
  const nx = (n.x / 100) * W;
  const ny = (n.y / 100) * H;
  const nw = (n.largura / 100) * W;
  const nh = (n.altura / 100) * H;
  desenharNome(ctx, c.nome, nx, ny, nw, nh);

  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => {
      if (b) resolve(b);
      else reject(new Error('Não consegui gerar a imagem.'));
    }, 'image/jpeg', 0.92);
  });
}